import * as tf from "@tensorflow/tfjs";

// Threshold: below this, prediction will be considered "Unknown" for production use
const UNKNOWN_THRESHOLD = 0.5;

// Cache the loaded model and metadata for reuse
let model: tf.LayersModel | null = null;
let classLabels: string[] = [];

/**
 * Loads the TensorFlow.js Layers model and metadata from the public directory.
 */
export async function loadCustomModel() {
  if (!model) {
    // Load both model and metadata
    const [modelResponse, metadataResponse] = await Promise.all([
      tf.loadLayersModel("/models/plant-disease/model.json"),
      fetch("/models/plant-disease/metadata.json")
    ]);
    
    model = modelResponse;
    const metadata = await metadataResponse.json();
    classLabels = metadata.labels;
    
    console.log(`✅ Loaded Teachable Machine model with ${classLabels.length} classes`);
  }
  return model;
}

/**
 * Predicts the plant disease given an image element.
 * Handles unknown or low-confidence cases.
 * @param image HTMLImageElement or HTMLCanvasElement to classify
 * @returns Array of predictions: {className: string, probability: number}[]
 */
export async function predictPlantDisease(
  image: HTMLImageElement | HTMLCanvasElement
): Promise<{ className: string; probability: number }[]> {
  const mdl = await loadCustomModel();

  // Teachable Machine expects 224x224 input
  const tensor = tf.browser.fromPixels(image)
    .resizeNearestNeighbor([224, 224])
    .toFloat()
    .div(tf.scalar(255))
    .expandDims();

  // Prediction
  const prediction = mdl.predict(tensor) as tf.Tensor;
  const scores = await prediction.data();

  // Map each output index to a class label and probability
  const result = Array.from(scores)
    .map((prob, i) => ({
      className: classLabels[i],
      probability: prob as number
    }))
    .sort((a, b) => b.probability - a.probability);

  // If max probability is low, return 'Unknown' as top suggestion
  if (result[0].probability < UNKNOWN_THRESHOLD) {
    return [
      { className: "Unknown", probability: result[0].probability },
      ...result
    ];
  }

  return result;
}

/**
 * Utility to check if the highest confidence is below threshold ("unknown" case).
 * @param predictions Array of predictions as returned by predictPlantDisease
 */
export function isPredictionUnknown(predictions: { className: string; probability: number }[]): boolean {
  if (!predictions.length) return true;
  // If the top class is "Unknown", it's an unknown prediction
  return predictions[0].className === "Unknown";
}